import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import { createArtist, getArtists } from "../../api/artistApi";
import { PlusIcon } from "@heroicons/react/outline";
import ArtistCreateModal from "./ArtistCreateModal";

const ArtistsPage = () => {
  const navigate = useNavigate();

  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);

  const fetchArtists = async () => {
    try {
      setLoading(true);
      const data = await getArtists();
      const list = Array.isArray(data) ? data : data.results || [];
      setArtists(list);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to load artists",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, []);

  const handleCreateArtist = async (data) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("stage_name", data.stage_name);
      formData.append("real_name", data.real_name);
      formData.append("profile_picture", data.profile_picture);
      await createArtist(formData);

      setToast({
        type: "success",
        message: "Artist created successfully",
      });

      setShowAddModal(false);
      fetchArtists();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setLoading(false)
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Artists</h1>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Add Artist
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {artists.map((artist) => (
          <div
            key={artist.id}
            className="bg-slate-900/60 border border-white/10 rounded-xl p-4 flex flex-col"
          >
            {/* Image */}
            <div className="w-full h-40 bg-slate-800 rounded-lg flex items-center justify-center mb-4 overflow-hidden">
              {artist.profile_picture ? (
                <img
                  src={artist.profile_picture}
                  alt={artist.stage_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-slate-500 text-sm">No Image</span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 space-y-1">
              <p className="text-white font-medium">
                {artist.stage_name || "-"}
              </p>
              <p className="text-slate-400 text-sm">
                {artist.real_name || "-"}
              </p>

              <div className="text-sm text-slate-400 mt-2 space-y-1">
                <p>
                  Active Bookings:{" "}
                  <span className="text-white">
                    {artist.active_bookings_count ?? 0}
                  </span>
                </p>
                <p>
                  Next Booking:{" "}
                  <span className="text-white">
                    {artist.next_booking_date || "-"}
                  </span>
                </p>
              </div>
            </div>

            {/* Action */}
            <button
              onClick={() => navigate(`/dashboard/artists/${artist.id}`)}
              className="mt-4 px-3 py-2 rounded-lg text-sm text-white bg-slate-800 hover:bg-slate-700 transition"
            >
              View Profile
            </button>
          </div>
        ))}

        {!artists.length && (
          <div className="col-span-full text-center text-slate-400 py-10">
            No artists found
          </div>
        )}
      </div>

      <ArtistCreateModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateArtist}
        loading={false}
      />

      {/* Toast */}
      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default ArtistsPage;
